import { Icon } from "@iconify/react";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import optionsApi from "../../api/optionsApi";
import { useIsRulezeroUser } from "../../utils/useIsRulezeroUser";
import { isOperationalControlFeatureEnabled } from "../../utils/operationalControlFeature";
import { useAuthStore } from "../../store";
import { HStack } from "../utils";

type AccessPolicy = {
  revision: number;
  incomingAccess: "ALLOW" | "READ_ONLY" | "BLOCK";
  sessionAction: "GRACEFUL_LOCK" | "REVOKE_NOW";
  accessMessage?: string;
  isExempt?: boolean;
};

type AdmissionState = "allowed" | "checking" | "unavailable";

export const OPERATIONAL_CONTROL_RESTRICTION_KEY =
  "operationalControlRestriction";

export default function OperationalControlAdmissionGate({
  children,
}: {
  children: ReactNode;
}) {
  const operationalControlEnabled = isOperationalControlFeatureEnabled();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isRulezeroUser = useIsRulezeroUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<AdmissionState>("checking");
  const [policy, setPolicy] = useState<AccessPolicy | undefined>();

  const verifyAdmission = useCallback(
    async (showBlockingState = false) => {
      if (!operationalControlEnabled) return;
      if (
        !isAuthenticated ||
        isRulezeroUser ||
        location.pathname === "/access-restricted"
      ) {
        setState("allowed");
        return;
      }
      if (showBlockingState) setState("checking");
      try {
        const response = await optionsApi.get(
          "/v1/operational-controls/session-status"
        );
        const nextPolicy = response.data?.data as AccessPolicy | undefined;
        if (!nextPolicy)
          throw new Error("Operational control status is missing");
        setPolicy(nextPolicy);
        if (!nextPolicy.isExempt && nextPolicy.incomingAccess === "BLOCK") {
          sessionStorage.setItem(
            OPERATIONAL_CONTROL_RESTRICTION_KEY,
            JSON.stringify({
              revision: nextPolicy.revision,
              sessionAction: nextPolicy.sessionAction,
              accessMessage: nextPolicy.accessMessage,
              expiresAt:
                nextPolicy.sessionAction === "GRACEFUL_LOCK"
                  ? Date.now() + 60_000
                  : undefined,
            })
          );
          navigate("/access-restricted", { replace: true });
          return;
        }
        setState("allowed");
      } catch {
        // A background recheck must never unmount a healthy application. The
        // backend still rejects restricted requests, and a foreground admission
        // check will reconcile the UI when the context changes.
        if (showBlockingState) setState("unavailable");
      }
    },
    [
      isAuthenticated,
      isRulezeroUser,
      location.pathname,
      navigate,
      operationalControlEnabled,
    ]
  );

  useEffect(() => {
    if (!operationalControlEnabled) {
      return undefined;
    }
    verifyAdmission().catch(() => undefined);
    const onCompanyContextChange = () => {
      verifyAdmission().catch(() => undefined);
    };
    window.addEventListener("company-context-changed", onCompanyContextChange);
    window.addEventListener(
      "operational-control-session-revoked",
      onCompanyContextChange
    );
    const interval = window.setInterval(() => {
      verifyAdmission().catch(() => undefined);
    }, 30_000);
    return () => {
      window.removeEventListener(
        "company-context-changed",
        onCompanyContextChange
      );
      window.removeEventListener(
        "operational-control-session-revoked",
        onCompanyContextChange
      );
      window.clearInterval(interval);
    };
  }, [operationalControlEnabled, verifyAdmission]);

  if (!operationalControlEnabled) return <>{children}</>;

  if (
    !isAuthenticated ||
    isRulezeroUser ||
    location.pathname === "/access-restricted"
  ) {
    return <>{children}</>;
  }

  return (
    <>
      {!policy?.isExempt && policy?.incomingAccess === "READ_ONLY" ? (
        <HStack
          className="fixed inset-x-0 top-0 z-[9999] items-center justify-center gap-2 bg-amber-100 px-4 py-2 text-center text-sm text-gray-800"
          role="status"
        >
          <Icon aria-hidden="true" icon="lucide:lock-keyhole" width={18} />
          <span>
            {policy.accessMessage ||
              "Your account is in read-only mode. Changes and requests are temporarily disabled."}
          </span>
        </HStack>
      ) : null}
      {children}
    </>
  );
}
